import {
  ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS,
  ACTIONFLOW_ONTOLOGY_V1_CONTRACT,
  ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS,
} from "./actionflow-contract-ontology";
import { validateActionFlowApplicationArchetypeCorpus } from "./actionflow-contract-validators";

export const ACTIONFLOW_ONTOLOGY_PLANNING_RESULT_SCHEMA_VERSION =
  "actionflow-ontology-planning-result.v1";

type ActionFlowArchetype =
  (typeof ACTIONFLOW_ONTOLOGY_V1_CONTRACT.applicationArchetypes)[number];
type ActionFlowScenario =
  (typeof ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS)[number];
type ActionFlowPrimitiveKind = keyof typeof ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS;

export interface ActionFlowOntologyRequiredAxes {
  primitiveKinds: string[];
  stateScopes: string[];
  effectBoundaryKinds: string[];
  stateRelationKinds: string[];
  paramRoles: string[];
  outcomeCriterionKinds: string[];
  readinessStages: string[];
  failureModes: string[];
  recoveryStrategies: string[];
  guardPolicies: string[];
}

export type ActionFlowOntologyGapCode =
  | "invalid-corpus"
  | "retrieval-gap"
  | "unsupported-primitive"
  | "missing-state-scope"
  | "missing-effect-boundary"
  | "missing-state-relation"
  | "missing-param-role";

export interface ActionFlowOntologyGap {
  code: ActionFlowOntologyGapCode;
  severity: "error" | "warning";
  scenarioId?: string;
  message: string;
  missing: string[];
  recoveryStrategies: string[];
  evidenceRefs: string[];
}

export interface ActionFlowOntologyScenarioMatch {
  scenarioId: string;
  archetype: ActionFlowArchetype;
  score: number;
  confidence: number;
  matchedSignals: string[];
  requiredAxes: ActionFlowOntologyRequiredAxes;
  gaps: ActionFlowOntologyGap[];
  ontologySubject: string;
  evidenceRefs: string[];
}

export interface ActionFlowOntologyPlanningInput {
  prompt?: string;
  appName?: string;
  description?: string;
  blockTypes?: readonly string[];
  blockNames?: readonly string[];
  binds?: readonly string[];
  datasourceNames?: readonly string[];
  requestedScenarioIds?: readonly string[];
  maxScenarios?: number;
  minScore?: number;
  availablePrimitiveKinds?: readonly string[];
  availableStateScopes?: readonly string[];
  availableEffectBoundaryKinds?: readonly string[];
  availableStateRelationKinds?: readonly string[];
  availableParamRoles?: readonly string[];
}

export type ActionFlowOntologyPlanningStatus = "matched" | "partial" | "gap";

export interface ActionFlowOntologyPlanningResult {
  schemaVersion: typeof ACTIONFLOW_ONTOLOGY_PLANNING_RESULT_SCHEMA_VERSION;
  contractVersion: typeof ACTIONFLOW_ONTOLOGY_V1_CONTRACT.version;
  generatedAt: string;
  status: ActionFlowOntologyPlanningStatus;
  valid: boolean;
  publishBlocked: boolean;
  inputDigest: {
    prompt?: string;
    appName?: string;
    description?: string;
    blockTypes: string[];
    blockNames: string[];
    binds: string[];
    datasourceNames: string[];
  };
  selectedScenarioIds: string[];
  selectedArchetypes: ActionFlowArchetype[];
  selectedOntologySubjects: string[];
  matches: ActionFlowOntologyScenarioMatch[];
  rejectedMatches: Array<Pick<ActionFlowOntologyScenarioMatch, "scenarioId" | "archetype" | "score" | "confidence" | "matchedSignals" | "ontologySubject">>;
  coverage: ActionFlowOntologyRequiredAxes;
  gaps: ActionFlowOntologyGap[];
  evidenceRefs: string[];
}

const DEFAULT_STATE_SCOPES = [
  "local",
  "appData",
  "computed",
  "datasource",
  "route",
  "env",
  "props",
  "_results",
] as const;

const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "app",
  "application",
  "be",
  "before",
  "by",
  "can",
  "data",
  "for",
  "from",
  "into",
  "is",
  "of",
  "or",
  "see",
  "state",
  "the",
  "to",
  "user",
  "with",
  "without",
]);

const ARCHETYPE_ALIASES: Record<ActionFlowArchetype, readonly string[]> = {
  "cart-checkout": [
    "cart",
    "checkout",
    "product",
    "quantity",
    "unit price",
    "total",
    "add to cart",
    "payable",
    "menu item",
    "장바구니",
    "결제",
    "상품",
    "수량",
    "단가",
    "합계",
  ],
  "search-filter-table": [
    "search",
    "filter",
    "table",
    "row",
    "rows",
    "count",
    "data table",
    "results",
    "검색",
    "필터",
    "테이블",
    "목록",
  ],
  "crud-admin": [
    "crud",
    "admin",
    "record",
    "create",
    "update",
    "delete",
    "business record",
    "backoffice",
    "관리자",
    "수정",
    "삭제",
  ],
  "route-detail-hydration": [
    "route",
    "detail",
    "params",
    "hydration",
    "entity",
    "product detail",
    "상세",
    "라우트",
    "파라미터",
  ],
  "auth-gated-mutation": [
    "auth",
    "permission",
    "role",
    "guard",
    "protected",
    "unauthorized",
    "rbac",
    "approval",
    "권한",
    "인증",
    "보호",
    "승인",
  ],
  "offline-storage-sync": [
    "offline",
    "local storage",
    "persist",
    "save locally",
    "storage",
    "sync",
    "오프라인",
    "로컬 저장",
    "저장소",
    "동기화",
  ],
  "multi-step-wizard": [
    "wizard",
    "multi-step",
    "step",
    "onboarding",
    "draft",
    "signup",
    "단계",
    "가입",
    "초안",
  ],
  // `file` / `파일` are deliberately ABSENT. They name a noun, not this
  // archetype's behaviour, and as bare aliases they scored the archetype for
  // `profile`, `file a complaint`, `파일럿`, and `파일 관리 앱` — none of which
  // upload or download anything. The behaviour verbs below carry the meaning;
  // the compounds keep the noun only where a verb is attached to it.
  "file-upload-download": [
    "upload",
    "download",
    "attachment",
    "file upload",
    "file download",
    "file attachment",
    "attach a file",
    "csv import",
    "업로드",
    "다운로드",
    "첨부",
    "파일 업로드",
    "파일 다운로드",
    "파일 첨부",
  ],
  "notification-modal-flow": [
    "notification",
    "toast",
    "modal",
    "popup",
    "dialog",
    "alert",
    "알림",
    "토스트",
    "모달",
    "팝업",
  ],
  "contact-form-submission": [
    "contact",
    "contact form",
    "message form",
    "send message",
    "submission",
    "submit message",
    "inquiry",
    "문의",
    "문의 폼",
    "연락처",
    "메시지 보내기",
  ],
  "realtime-chat-subscription": [
    "realtime",
    "real-time",
    "chat",
    "subscription",
    "message",
    "collaborator",
    "live updates",
    // A newsletter is a subscription product delivered as a message, which is
    // what this archetype's userGoal already describes ("Send and receive …
    // messages with visible delivery state"). Added in both languages, as
    // every entry in this table is: `구독자들에게 유료로 뉴스레터를 보내고 싶어`
    // names a behaviour and earned 0.900 — one lone alias — because the
    // corpus had no word for the thing being sent. It was admitted anyway,
    // through its own appSpec's binds, until that channel was closed.
    "newsletter",
    "실시간",
    "채팅",
    "메시지",
    "구독",
    "뉴스레터",
  ],
  "booking-scheduling": [
    "booking",
    "reservation",
    "schedule",
    "slot",
    "calendar",
    "time slot",
    "scheduling",
    "appointment",
    "availability",
    "available time",
    "reserve",
    "예약",
    "일정",
    "시간",
    "슬롯",
  ],
  "report-export-workflow": [
    "report",
    "export",
    "pdf",
    "excel",
    "csv",
    "docx",
    "pptx",
    "hwp",
    "filtered source",
    "analyst",
    "보고서",
    "엑셀",
    "내보내기",
    "리포트",
  ],
};

export function planActionFlowOntology(
  input: ActionFlowOntologyPlanningInput,
): ActionFlowOntologyPlanningResult {
  const generatedAt = new Date().toISOString();
  const corpusValidation = validateActionFlowApplicationArchetypeCorpus();
  const corpusErrors = corpusValidation.ok
    ? []
    : [
        {
          code: "invalid-corpus" as const,
          severity: "error" as const,
          message:
            "ActionFlow application archetype corpus failed validation.",
          missing: corpusValidation.errors,
          recoveryStrategies: ["block-publish", "attach-gap-report"],
          evidenceRefs: ["validateActionFlowApplicationArchetypeCorpus"],
        },
      ];
  const ranked = ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS.map((scenario) =>
    scoreScenario(scenario, input),
  ).sort((a, b) => b.score - a.score || a.scenarioId.localeCompare(b.scenarioId));
  const maxScenarios = Math.max(1, input.maxScenarios ?? 3);
  const minScore = input.minScore ?? 1.2;
  const requested = new Set(input.requestedScenarioIds ?? []);
  const selected = ranked
    .filter((match) => requested.has(match.scenarioId) || match.score >= minScore)
    .slice(0, maxScenarios)
    .map((match) => attachCoverageGaps(match, input));
  const selectedMatches =
    selected.length > 0 ? selected : requestedMatchesOnly(ranked, requested, input);
  const retrievalGaps =
    selectedMatches.length > 0
      ? []
      : [
          {
            code: "retrieval-gap" as const,
            severity: "error" as const,
            message:
              "No ActionFlow application archetype satisfied the behavior intent.",
            missing: ["applicationArchetype"],
            recoveryStrategies: ["block-publish", "attach-gap-report"],
            evidenceRefs: [
              "ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS",
              "appSpec.description",
            ],
          },
        ];
  const gaps = [
    ...corpusErrors,
    ...retrievalGaps,
    ...selectedMatches.flatMap((match) => match.gaps),
  ];
  const coverage = mergeAxes(selectedMatches.map((match) => match.requiredAxes));
  const publishBlocked = gaps.some((gap) => gap.severity === "error");
  const status: ActionFlowOntologyPlanningStatus =
    selectedMatches.length === 0
      ? "gap"
      : publishBlocked
        ? "partial"
        : "matched";

  return {
    schemaVersion: ACTIONFLOW_ONTOLOGY_PLANNING_RESULT_SCHEMA_VERSION,
    contractVersion: ACTIONFLOW_ONTOLOGY_V1_CONTRACT.version,
    generatedAt,
    status,
    valid: !publishBlocked && selectedMatches.length > 0,
    publishBlocked,
    inputDigest: {
      ...(input.prompt ? { prompt: input.prompt } : {}),
      ...(input.appName ? { appName: input.appName } : {}),
      ...(input.description ? { description: input.description } : {}),
      blockTypes: dedupe([...(input.blockTypes ?? [])]),
      blockNames: dedupe([...(input.blockNames ?? [])]),
      binds: dedupe([...(input.binds ?? [])]),
      datasourceNames: dedupe([...(input.datasourceNames ?? [])]),
    },
    selectedScenarioIds: selectedMatches.map((match) => match.scenarioId),
    selectedArchetypes: selectedMatches.map((match) => match.archetype),
    selectedOntologySubjects: selectedMatches.map(
      (match) => match.ontologySubject,
    ),
    matches: selectedMatches,
    rejectedMatches: ranked
      .filter(
        (match) =>
          !selectedMatches.some((selectedMatch) => selectedMatch.scenarioId === match.scenarioId),
      )
      .slice(0, 5)
      .map((match) => ({
        scenarioId: match.scenarioId,
        archetype: match.archetype,
        score: match.score,
        confidence: match.confidence,
        matchedSignals: match.matchedSignals,
        ontologySubject: match.ontologySubject,
      })),
    coverage,
    gaps,
    evidenceRefs: [
      "ACTIONFLOW_ONTOLOGY_V1_CONTRACT",
      "ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS",
      "ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS",
      "validateActionFlowApplicationArchetypeCorpus",
    ],
  };
}

function requestedMatchesOnly(
  ranked: ActionFlowOntologyScenarioMatch[],
  requested: ReadonlySet<string>,
  input: ActionFlowOntologyPlanningInput,
): ActionFlowOntologyScenarioMatch[] {
  if (requested.size === 0) return [];
  return ranked
    .filter((match) => requested.has(match.scenarioId))
    .map((match) => attachCoverageGaps(match, input));
}

/**
 * Three scoring channels, and one rule that governs all of them: a score is
 * evidence that the PERSON named this archetype's behaviour. Nothing this
 * pipeline produced, and nothing the corpus entry itself requires, may pay
 * into that.
 *
 * Measured on `origin/main` before this rule existed. `planActionFlowOntology`
 * is called by Studio with the appSpec, not the raw prompt
 * (`ontology-retrieval-evidence.ts` → `buildActionFlowOntologyPlanTrace`), so
 * `binds` carries whatever the decompose stage emitted. Driven through that
 * shape with the ordinary binds any Cogo app has — `local.query`,
 * `appData.items`, `computed.total` — ALL SIX of this file's
 * "still refuses …" prompts came back `status=matched` on `cart-checkout` at
 * 1.700 against the 1.2 bar, every one of them at the identical score:
 *
 *     compile shaders for a raytracer            ADMIT 1.700
 *     시집을 위한 아름다운 활자 조판을 원해          ADMIT 1.700
 *     an inventory app for my bookstore          ADMIT 1.700
 *     a recipe book app                          ADMIT 1.700
 *     an app to preserve family photos           ADMIT 1.700
 *     3D 렌더링 셰이더 컴파일러                    ADMIT 1.700
 *
 * Identical, because the prompt contributed nothing at all. The 1.700 was
 * `alias:total` (0.900, from the bind `computed.total`) plus `token:local`,
 * `token:appdata`, `token:computed`, `token:total` (0.200 each) — the
 * canonical expression-prefix enum, which is closed and which EVERY Cogo app
 * uses by construction. Those six refusals held only because the tests drove
 * the prompt alone, which no caller does.
 *
 * The three fixes, each measurable on its own:
 *
 *  1. `scenarioText` drops the `required*` axes. Those axes are this
 *     function's OUTPUT — the state scopes, effect boundaries, param roles and
 *     primitive kinds the compiler must supply if this archetype is chosen —
 *     not evidence for choosing it. Tokenized, they are `local`, `appdata`,
 *     `computed`, `datasource`, `value`, `target`, `source`, `result`: the
 *     runtime's addressing vocabulary, present in every app ever generated.
 *
 *  2. The curated-phrase channel reads `intentText`, not `text`. Aliases are
 *     the strongest signal here (0.900/1.400 against a 1.2 bar — one alias is
 *     three quarters of an admission), and letting `binds` satisfy them lets
 *     the decompose stage vouch for its own admission. In the offline lane
 *     that circuit is literal: `behaviourEvidencedByPrompt` picks an archetype
 *     from an `alias:` hit, `stateBinds` then emits `local.<x>`/`appData.<x>`
 *     BECAUSE it did (`e2e-mock-decompose-planner.ts`), and those binds came
 *     back as independent support for the same archetype.
 *
 *  3. A word already paid for as a phrase is not paid again as a token, and a
 *     scenario that names itself twice is read once.
 *
 * After all three, the six refuse at 0.600 through the same appSpec shape.
 * The regression controls are unmoved: prompt-only scores are identical except
 * where (3) removes a genuine duplicate, and every archetype in the corpus is
 * still reachable from its representative intent.
 */
function scoreScenario(
  scenario: ActionFlowScenario,
  input: ActionFlowOntologyPlanningInput,
): ActionFlowOntologyScenarioMatch {
  // What the PERSON asked for. Everything else on `input` is what the
  // decompose stage produced FROM this, downstream of it in the same pipeline.
  const intentText = normalizeText([
    input.prompt,
    input.appName,
    input.description,
  ]);
  // No `text` union any more. Every term below reads `intentText`: the rule
  // stated above ("everything else on `input` is what the decompose stage
  // produced FROM this") holds for the whole score, not only for the strongest
  // channel. Derived fields still reach `detectCoverageGaps` — they are
  // evidence of what the plan CONTAINS, which is a coverage question, never
  // evidence for WHICH archetype the person asked for.
  // The corpus's BEHAVIOUR prose only. The `required*` axes are deliberately
  // absent — see the note above `scoreScenario`.
  const scenarioText = normalizeText([
    scenario.scenarioId,
    scenario.archetype,
    scenario.persona,
    scenario.userGoal,
    scenario.userValue,
  ]);
  const scenarioTokens = tokenize(scenarioText);
  const inputTokens = new Set(tokenize(intentText));
  const matchedSignals: string[] = [];
  /** Input words already paid for through a curated phrase. */
  const creditedByPhrase = new Set<string>();
  let score = 0;

  const creditPhrase = (phrase: string, weight: number, signal: string) => {
    // `intentText`, never `text`: a phrase must be named by the person, not
    // by the plan that was derived from them.
    if (!containsPhrase(intentText, phrase)) return;
    score += weight;
    matchedSignals.push(signal);
    for (const word of tokenize(normalizeText([phrase]))) {
      creditedByPhrase.add(word);
    }
  };

  // `dedupe`: every scenario in the corpus names itself twice
  // (`scenarioId === archetype` for all 13), and one occurrence of the name in
  // the prompt is one piece of evidence, not two.
  for (const phrase of dedupe([scenario.scenarioId, scenario.archetype])) {
    creditPhrase(phrase, 4, `archetype:${phrase}`);
  }

  for (const phrase of ARCHETYPE_ALIASES[scenario.archetype]) {
    creditPhrase(
      phrase,
      phrase.includes(" ") || phrase.includes("-") ? 1.4 : 0.9,
      `alias:${phrase}`,
    );
  }

  for (const token of scenarioTokens) {
    if (!inputTokens.has(token)) continue;
    // The alias list and the scenario prose are two encodings of one fact:
    // `crud-admin` lists the alias `create` AND opens its userGoal with
    // "Create or update…". Without this guard one occurrence of `create` in
    // the prompt is paid twice, at 0.900 and again at 0.200.
    if (creditedByPhrase.has(token)) continue;
    score += 0.2;
    matchedSignals.push(`token:${token}`);
  }

  // The block-type channel is gone. `blockTypes` is decompose output in its
  // entirety, so crediting it let a plan vouch for the archetype it was
  // generated for — with no prompt at all, `blockTypes: ["notification",
  // "modal"]` was ADMITTED at 1.400. A block type the person actually named is
  // already paid, once, through the phrase or token channel above.

  return {
    scenarioId: scenario.scenarioId,
    archetype: scenario.archetype,
    score: Number(score.toFixed(3)),
    confidence: Number(Math.min(1, score / 6).toFixed(3)),
    matchedSignals: dedupe(matchedSignals).slice(0, 16),
    requiredAxes: requiredAxesForScenario(scenario),
    gaps: [],
    ontologySubject: `afcase:${scenario.scenarioId}`,
    evidenceRefs: [
      `ACTIONFLOW_APPLICATION_ARCHETYPE_CORPUS.${scenario.scenarioId}`,
      `packages/cogo-kg-browser/fixtures/actionflow-application-archetypes-v1.ttl#${scenario.scenarioId}`,
    ],
  };
}

function attachCoverageGaps(
  match: ActionFlowOntologyScenarioMatch,
  input: ActionFlowOntologyPlanningInput,
): ActionFlowOntologyScenarioMatch {
  return {
    ...match,
    gaps: detectCoverageGaps(match, input),
  };
}

function detectCoverageGaps(
  match: ActionFlowOntologyScenarioMatch,
  input: ActionFlowOntologyPlanningInput,
): ActionFlowOntologyGap[] {
  const availablePrimitiveKinds = new Set(
    input.availablePrimitiveKinds ??
      (Object.keys(ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS) as string[]),
  );
  const supportedBySelectedPrimitives = supportedCompilerAxes(
    match.requiredAxes.primitiveKinds.filter((kind) =>
      availablePrimitiveKinds.has(kind),
    ),
  );
  const availableStateScopes = new Set(
    input.availableStateScopes ?? DEFAULT_STATE_SCOPES,
  );
  const availableEffectBoundaryKinds = new Set(
    input.availableEffectBoundaryKinds ??
      [...supportedBySelectedPrimitives.effectBoundaryKinds],
  );
  const availableStateRelationKinds = new Set(
    input.availableStateRelationKinds ??
      [...supportedBySelectedPrimitives.stateRelationKinds],
  );
  const availableParamRoles = new Set(
    input.availableParamRoles ?? [...supportedBySelectedPrimitives.paramRoles],
  );
  const gaps: ActionFlowOntologyGap[] = [];
  const addGap = (
    code: ActionFlowOntologyGapCode,
    message: string,
    missing: string[],
  ) => {
    if (missing.length === 0) return;
    gaps.push({
      code,
      severity: "error",
      scenarioId: match.scenarioId,
      message,
      missing,
      recoveryStrategies: recoveryStrategiesForGap(match),
      evidenceRefs: [
        ...match.evidenceRefs,
        "ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS",
      ],
    });
  };

  addGap(
    "unsupported-primitive",
    "Selected archetype requires primitive kinds that the compiler cannot target.",
    match.requiredAxes.primitiveKinds.filter(
      (kind) => !availablePrimitiveKinds.has(kind),
    ),
  );
  addGap(
    "missing-state-scope",
    "Selected archetype requires state scopes unavailable to the current topology compiler.",
    match.requiredAxes.stateScopes.filter((scope) => !availableStateScopes.has(scope)),
  );
  addGap(
    "missing-effect-boundary",
    "Selected archetype requires effect boundaries not covered by selected primitive compiler contracts.",
    match.requiredAxes.effectBoundaryKinds.filter(
      (kind) => !availableEffectBoundaryKinds.has(kind),
    ),
  );
  addGap(
    "missing-state-relation",
    "Selected archetype requires state relations not covered by selected primitive compiler contracts.",
    match.requiredAxes.stateRelationKinds.filter(
      (kind) => !availableStateRelationKinds.has(kind),
    ),
  );
  addGap(
    "missing-param-role",
    "Selected archetype requires compiler param roles not covered by selected primitive compiler contracts.",
    match.requiredAxes.paramRoles.filter((role) => !availableParamRoles.has(role)),
  );

  return gaps;
}

function supportedCompilerAxes(primitiveKinds: readonly string[]): {
  effectBoundaryKinds: Set<string>;
  stateRelationKinds: Set<string>;
  paramRoles: Set<string>;
} {
  const effectBoundaryKinds = new Set<string>();
  const stateRelationKinds = new Set<string>();
  const paramRoles = new Set<string>();
  for (const primitiveKind of primitiveKinds) {
    const compiler =
      ACTIONFLOW_PRIMITIVE_COMPILER_TARGETS[primitiveKind as ActionFlowPrimitiveKind];
    if (!compiler) continue;
    compiler.effectBoundaryKinds.forEach((kind) => effectBoundaryKinds.add(kind));
    compiler.stateRelationKinds.forEach((kind) => stateRelationKinds.add(kind));
    compiler.paramRoles.forEach((role) => paramRoles.add(role));
  }
  return { effectBoundaryKinds, stateRelationKinds, paramRoles };
}

function recoveryStrategiesForGap(
  match: ActionFlowOntologyScenarioMatch,
): string[] {
  return match.requiredAxes.recoveryStrategies.length > 0
    ? match.requiredAxes.recoveryStrategies
    : ["block-publish", "attach-gap-report"];
}

function requiredAxesForScenario(
  scenario: ActionFlowScenario,
): ActionFlowOntologyRequiredAxes {
  return {
    primitiveKinds: [...scenario.requiredPrimitiveKinds],
    stateScopes: [...scenario.requiredStateScopes],
    effectBoundaryKinds: [...scenario.requiredEffectBoundaryKinds],
    stateRelationKinds: [...scenario.requiredStateRelationKinds],
    paramRoles: [...scenario.requiredParamRoles],
    outcomeCriterionKinds: [...scenario.requiredOutcomeCriterionKinds],
    readinessStages: [...scenario.requiredReadinessStages],
    failureModes: [...scenario.failureModes],
    recoveryStrategies: [...scenario.recoveryStrategies],
    guardPolicies: [...scenario.guardPolicies],
  };
}

function mergeAxes(
  axes: readonly ActionFlowOntologyRequiredAxes[],
): ActionFlowOntologyRequiredAxes {
  return {
    primitiveKinds: mergeAxis(axes, "primitiveKinds"),
    stateScopes: mergeAxis(axes, "stateScopes"),
    effectBoundaryKinds: mergeAxis(axes, "effectBoundaryKinds"),
    stateRelationKinds: mergeAxis(axes, "stateRelationKinds"),
    paramRoles: mergeAxis(axes, "paramRoles"),
    outcomeCriterionKinds: mergeAxis(axes, "outcomeCriterionKinds"),
    readinessStages: mergeAxis(axes, "readinessStages"),
    failureModes: mergeAxis(axes, "failureModes"),
    recoveryStrategies: mergeAxis(axes, "recoveryStrategies"),
    guardPolicies: mergeAxis(axes, "guardPolicies"),
  };
}

function mergeAxis(
  axes: readonly ActionFlowOntologyRequiredAxes[],
  key: keyof ActionFlowOntologyRequiredAxes,
): string[] {
  return dedupe(axes.flatMap((axis) => axis[key]));
}

function normalizeText(values: readonly (string | undefined)[]): string {
  return values
    .filter((value): value is string => Boolean(value?.trim()))
    .join(" ")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * A phrase counts only where it BEGINS a word.
 *
 * Plain substring containment made the corpus answer for text it has no
 * business answering for: `컴파일러` contains `파일`, `disappointment` contains
 * `record`, `discount` contains `count`, `profile` contains `file`, and
 * `context` (this corpus's own prose) contains the `text` block type. Every
 * one of those is a mid-word accident, and the accident is what this anchor
 * removes.
 *
 * Matching stays OPEN to the right, and that is load-bearing rather than
 * incidental. Korean is agglutinative — the corpus's own recognised prompt is
 * `구독자들에게…`, where the alias `구독` carries five more syllables of
 * particles before the next space — and English inflects the same way, so
 * `record` must still answer for `records` and `create` for `creating`. A
 * symmetric word-boundary rule (`\bphrase\b`) would refuse all of those; it
 * is not a safe replacement for substring matching here, only a safe
 * restriction of where a match may start.
 *
 * A script change is itself a word start (`csv파일`, `2단계`), since scripts
 * that do not space their words still switch alphabet at the seam.
 */
function containsPhrase(text: string, phrase: string): boolean {
  const needle = phrase.toLowerCase().trim();
  if (needle.length === 0) return false;
  const headClass = characterClass(needle[0]!);
  if (headClass === "other") return text.includes(needle);
  let from = 0;
  for (;;) {
    const at = text.indexOf(needle, from);
    if (at < 0) return false;
    if (at === 0) return true;
    if (characterClass(text[at - 1]!) !== headClass) return true;
    from = at + 1;
  }
}

const HANGUL = /\p{Script=Hangul}/u;
const HAN = /\p{Script=Han}/u;
const KANA = /\p{Script=Hiragana}|\p{Script=Katakana}/u;
const LETTER = /\p{L}/u;
const NUMBER = /\p{N}/u;

/**
 * Word-continuation class. Two characters of the same class are inside one
 * word; anything else is a seam a phrase may start at.
 */
function characterClass(character: string): string {
  if (HANGUL.test(character)) return "hangul";
  if (HAN.test(character)) return "han";
  if (KANA.test(character)) return "kana";
  if (LETTER.test(character)) return "letter";
  if (NUMBER.test(character)) return "number";
  return "other";
}

function tokenize(value: string): string[] {
  return dedupe(
    [...value.matchAll(/[\p{L}\p{N}]+/gu)]
      .map((match) => match[0].toLowerCase())
      .filter((token) => token.length > 1 && !STOP_WORDS.has(token)),
  );
}

function dedupe<T extends string>(values: readonly T[]): T[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}
